import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { MapPin, Search, Loader2, DollarSign, Clock, ChevronDown, Edit2 } from 'lucide-react';
import { DeliveryAddress, Customer } from '@/types';
import { toast } from '@/hooks/use-toast';
import { formatCurrency, cn } from '@/lib/utils';

interface DeliveryAddressFormProps {
  address: DeliveryAddress | undefined;
  onChange: (address: DeliveryAddress) => void;
  customer?: Customer | null;
  deliveryFee: number;
  onDeliveryFeeChange: (fee: number) => void;
  estimatedTime: number;
  onEstimatedTimeChange: (time: number) => void;
  deliveryNotes?: string;
  onDeliveryNotesChange?: (notes: string) => void;
}

const DeliveryAddressForm: React.FC<DeliveryAddressFormProps> = ({
  address,
  onChange,
  customer,
  deliveryFee,
  onDeliveryFeeChange,
  estimatedTime,
  onEstimatedTimeChange,
  deliveryNotes,
  onDeliveryNotesChange,
}) => {
  const [isLoadingCep, setIsLoadingCep] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [localAddress, setLocalAddress] = useState<DeliveryAddress>(
    address || {
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      zipCode: '',
      reference: '',
    }
  );

  const hasAddress = localAddress.street && localAddress.number;

  // Preencher com dados do cliente quando selecionado
  useEffect(() => {
    if (customer) {
      setLocalAddress((prev) => ({
        street: customer.address || prev.street,
        number: customer.addressNumber || prev.number,
        complement: customer.complement || prev.complement,
        neighborhood: customer.neighborhood || prev.neighborhood,
        city: customer.city || prev.city,
        state: customer.state || prev.state,
        zipCode: customer.zipCode || prev.zipCode,
        reference: customer.reference || prev.reference,
      }));
    }
  }, [customer]);

  // Sincronizar com prop externa
  useEffect(() => {
    if (address) {
      setLocalAddress(address);
    }
  }, [address]);

  const handleChange = (field: keyof DeliveryAddress, value: string) => {
    const updated = { ...localAddress, [field]: value };
    setLocalAddress(updated);
    onChange(updated);
  };

  const handleCepSearch = async () => {
    const cep = localAddress.zipCode.replace(/\D/g, '');
    
    if (cep.length !== 8) {
      toast({
        title: 'CEP inválido',
        description: 'Digite um CEP com 8 dígitos',
        variant: 'destructive',
      });
      return;
    }

    setIsLoadingCep(true);
    
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        toast({
          title: 'CEP não encontrado',
          description: 'Verifique o CEP e tente novamente',
          variant: 'destructive',
        });
        return;
      }
      
      const updated: DeliveryAddress = {
        ...localAddress,
        street: data.logradouro || localAddress.street,
        neighborhood: data.bairro || localAddress.neighborhood,
        city: data.localidade || localAddress.city,
        state: data.uf || localAddress.state,
      };
      
      setLocalAddress(updated);
      onChange(updated);
      
      toast({
        title: 'Endereço encontrado',
        description: `${data.logradouro}, ${data.bairro} - ${data.localidade}/${data.uf}`,
      });
    } catch (error) {
      toast({
        title: 'Erro ao buscar CEP',
        description: 'Não foi possível consultar o CEP',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingCep(false);
    }
  };

  const formatCep = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    if (digits.length > 5) {
      return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    }
    return digits;
  };

  return (
    <Card className="border-green-200 bg-green-50/30">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button className="w-full p-3 flex items-center justify-between hover:bg-green-100/50 transition-colors rounded-t-lg">
            <div className="flex items-center gap-2 text-green-700">
              <MapPin className="h-5 w-5" />
              <span className="font-medium">Endereço de Entrega</span>
            </div>
            <div className="flex items-center gap-2">
              {hasAddress && !isOpen && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(true);
                  }}
                >
                  <Edit2 className="h-3 w-3 mr-1" />
                  Editar
                </Button>
              )}
              <ChevronDown className={cn(
                "h-5 w-5 text-green-600 transition-transform duration-200",
                isOpen && "rotate-180"
              )} />
            </div>
          </button>
        </CollapsibleTrigger>

        {/* Summary when closed */}
        {!isOpen && (
          <CardContent className="pt-0 pb-3">
            {hasAddress ? (
              <div className="text-sm text-green-800 space-y-1">
                <p>
                  {localAddress.street}, {localAddress.number}
                  {localAddress.complement && ` - ${localAddress.complement}`}
                </p>
                <p className="text-green-600">
                  {localAddress.neighborhood}, {localAddress.city}/{localAddress.state}
                </p>
                <div className="flex items-center gap-3 text-xs text-green-700 mt-2">
                  {deliveryFee > 0 && (
                    <span className="flex items-center gap-1">
                      <DollarSign className="h-3 w-3" />
                      {formatCurrency(deliveryFee)}
                    </span>
                  )}
                  {estimatedTime > 0 && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      ~{estimatedTime} min
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-green-600 italic">
                Clique para adicionar o endereço
              </p>
            )}
          </CardContent>
        )}

        <CollapsibleContent>
          <CardContent className="space-y-4 pt-0">
            {/* CEP com busca */}
            <div className="flex gap-2">
              <div className="flex-1">
                <Label htmlFor="zipCode">CEP</Label>
                <Input
                  id="zipCode"
                  placeholder="00000-000"
                  value={formatCep(localAddress.zipCode)}
                  onChange={(e) => handleChange('zipCode', e.target.value)}
                  className="bg-white"
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCepSearch}
                  disabled={isLoadingCep}
                  className="bg-white"
                >
                  {isLoadingCep ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Rua e Número */}
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label htmlFor="street">Rua</Label>
                <Input
                  id="street"
                  placeholder="Nome da rua"
                  value={localAddress.street}
                  onChange={(e) => handleChange('street', e.target.value)}
                  className="bg-white"
                />
              </div>
              <div>
                <Label htmlFor="number">Número</Label>
                <Input
                  id="number"
                  placeholder="Nº"
                  value={localAddress.number}
                  onChange={(e) => handleChange('number', e.target.value)}
                  className="bg-white"
                />
              </div>
            </div>

            {/* Complemento e Bairro */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="complement">Complemento</Label>
                <Input
                  id="complement"
                  placeholder="Apto, bloco..."
                  value={localAddress.complement || ''}
                  onChange={(e) => handleChange('complement', e.target.value)}
                  className="bg-white"
                />
              </div>
              <div>
                <Label htmlFor="neighborhood">Bairro</Label>
                <Input
                  id="neighborhood"
                  placeholder="Bairro"
                  value={localAddress.neighborhood}
                  onChange={(e) => handleChange('neighborhood', e.target.value)}
                  className="bg-white"
                />
              </div>
            </div>

            {/* Cidade e Estado */}
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label htmlFor="city">Cidade</Label>
                <Input
                  id="city"
                  placeholder="Cidade"
                  value={localAddress.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  className="bg-white"
                />
              </div>
              <div>
                <Label htmlFor="state">UF</Label>
                <Input
                  id="state"
                  placeholder="UF"
                  value={localAddress.state}
                  onChange={(e) => handleChange('state', e.target.value.toUpperCase().slice(0, 2))}
                  className="bg-white"
                  maxLength={2}
                />
              </div>
            </div>

            {/* Referência */}
            <div>
              <Label htmlFor="reference">Ponto de Referência</Label>
              <Input
                id="reference"
                placeholder="Ex: Próximo ao mercado..."
                value={localAddress.reference || ''}
                onChange={(e) => handleChange('reference', e.target.value)}
                className="bg-white"
              />
            </div>

            {/* Taxa de entrega e tempo estimado */}
            <div className="grid grid-cols-2 gap-2 pt-2 border-t">
              <div>
                <Label htmlFor="deliveryFee" className="flex items-center gap-1">
                  <DollarSign className="h-3 w-3" />
                  Taxa de Entrega
                </Label>
                <Input
                  id="deliveryFee"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0,00"
                  value={deliveryFee || ''}
                  onChange={(e) => onDeliveryFeeChange(parseFloat(e.target.value) || 0)}
                  className="bg-white"
                />
              </div>
              <div>
                <Label htmlFor="estimatedTime" className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Tempo Estimado (min)
                </Label>
                <Input
                  id="estimatedTime"
                  type="number"
                  min="0"
                  step="5"
                  placeholder="30"
                  value={estimatedTime || ''}
                  onChange={(e) => onEstimatedTimeChange(parseInt(e.target.value) || 0)}
                  className="bg-white"
                />
              </div>
            </div>

            {/* Observações de entrega */}
            {onDeliveryNotesChange && (
              <div>
                <Label htmlFor="deliveryNotes">Observações da Entrega</Label>
                <Textarea
                  id="deliveryNotes"
                  placeholder="Informações adicionais para o entregador..."
                  value={deliveryNotes || ''}
                  onChange={(e) => onDeliveryNotesChange(e.target.value)}
                  className="bg-white min-h-[60px]"
                />
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default DeliveryAddressForm;
